import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { toAbsoluteUrl } from '../../_dashboard/helpers'
import { createOne } from '../services/common_requests'
import { ImagePlacement, recommendedSizeFor } from '../utils/imagePlacements'

export interface SimpleSiteImageUploadProps {
  onImageAdded: (image: { url: string; _id: string }) => void
  uploadEndpoint?: string
  placement?: ImagePlacement | string
  recommendedSize?: string
  showSizeHint?: boolean
}

/**
 * Image upload without hotel/tour context (uses /images). No react-query dependency.
 */
const SimpleSiteImageUpload: React.FC<SimpleSiteImageUploadProps> = ({
  onImageAdded,
  uploadEndpoint = '/images',
  placement = 'general',
  recommendedSize,
  showSizeHint = true,
}) => {
  const intl = useIntl()
  const [preview, setPreview] = useState<string | null>(null)
  const sizeLabel = recommendedSize || recommendedSizeFor(placement)

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return
    const file = e.target.files[0]
    const reader = new FileReader()
    reader.onload = (ev) => setPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
    const formData = new FormData()
    formData.append('image', file)
    formData.append('type', 'general')
    formData.append('placement', String(placement))
    const endpoint = uploadEndpoint.includes('?')
      ? `${uploadEndpoint}&placement=${encodeURIComponent(String(placement))}`
      : `${uploadEndpoint}?placement=${encodeURIComponent(String(placement))}`
    try {
      const response = await createOne(formData, endpoint)
      if (response?.payload?.code === 2021 && response?.data) {
        onImageAdded({ url: response.data.url, _id: response.data._id })
        toast.success(
          intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.IMAGE_UPDATED_SUCCESSFULLY' }),
          { position: 'top-right', autoClose: 3000 },
        )
      } else {
        toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.IMAGE_UPDATED' }), {
          position: 'top-right',
        })
      }
    } catch {
      toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.IMAGE' }), {
        position: 'top-right',
      })
    }
  }

  return (
    <div className='d-inline-flex flex-column align-items-start'>
      <div className='image-container-dashed'>
        <div className='upload-input-container'>
          <input type='file' accept='image/*' onChange={handleChange} className='inpute' />
        </div>
        {preview ? (
          <img src={preview} alt='' className='preview-image-uploader' />
        ) : (
          <div className='image-container-placeholder'>
            <img src={toAbsoluteUrl('/media/icons/upload-image.png')} alt='' />
            <span>{intl.formatMessage({ id: 'COMMON.ADD' })}</span>
          </div>
        )}
      </div>
      {showSizeHint && sizeLabel ? (
        <div className='form-text text-muted mt-2 mb-0'>
          {intl.formatMessage({ id: 'COMMON.IMAGE.RECOMMENDED_SIZE' }, { size: sizeLabel })}
        </div>
      ) : null}
    </div>
  )
}

export default SimpleSiteImageUpload
